import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, PackageCheck, Trash2, Users, Loader2 } from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Card, Input, Button, Modal } from '../../Components/ui';
import { useConfirm, useToast } from '../../Components/Feedback';
import { useApi } from '../../api/useApi';

// Tela inicial de "Devolução de Materiais" — uniforme e outros itens que
// alunos que ficaram INATIVOS precisam devolver. Mesmo padrão de cartões da
// tela Termos, mas aqui as contagens/listas são só de alunos inativos (ver
// backend/devolucoes.js) — quem está ativo não aparece em lugar nenhum aqui.
function Devolucoes() {
  const navigate = useNavigate();
  const { institutionId } = useInstitution();
  const { token } = useAuth();
  const { podeCriar, podeExcluir } = usePermissao('/devolucoes');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();

  const api = useApi();

  const { data: itens = [], isLoading } = useQuery({
    queryKey: ['devolucoes', institutionId],
    queryFn: async () => {
      const res = await api.get('/api/devolucoes');
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const recarregar = () =>
    queryClient.invalidateQueries({ queryKey: ['devolucoes', institutionId] });

  // --- Criar ---
  const [modalAberto, setModalAberto] = useState(false);
  const [nomeForm, setNomeForm] = useState('');
  const [salvando, setSalvando] = useState(false);

  const criar = async (e) => {
    e.preventDefault();
    const nome = nomeForm.trim();
    if (!nome) return;
    setSalvando(true);
    try {
      const res = await api.post('/api/devolucoes', { nome });
      const criado = res?.data ?? res;
      setModalAberto(false);
      setNomeForm('');
      toast.success(res?.message || 'Item criado com sucesso.');
      recarregar();
      if (criado?.id) navigate(`/devolucoes/${criado.id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  // --- Apagar ---
  const apagar = async (e, item) => {
    e.stopPropagation();
    if (
      !(await confirm(
        `Apagar o item "${item.nome}"? Isso remove também o registro de quem já devolveu — não pode ser desfeito.`,
        { title: 'Apagar item', confirmText: 'Apagar' },
      ))
    )
      return;
    try {
      const res = await api.del(`/api/devolucoes/${item.id}`);
      toast.success(res?.message || 'Item apagado com sucesso.');
      recarregar();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Uniforme e outros materiais a devolver — só alunos inativos. Clique num item pra ver
            quem já devolveu.
          </p>
          {podeCriar && (
            <Button variant="primary" icon={Plus} onClick={() => setModalAberto(true)}>
              Novo Item
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : itens.length === 0 ? (
          <div className="text-center py-16 text-gray-400 dark:text-gray-500">
            <PackageCheck className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-700" />
            <p className="text-sm">Nenhum item criado ainda.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {itens.map((item) => {
              const pendentes = item.total_alunos - item.devolvidos;
              return (
                <Card
                  key={item.id}
                  className="cursor-pointer hover:border-blue-200 dark:hover:border-blue-800 transition-colors group"
                >
                  <div
                    onClick={() => navigate(`/devolucoes/${item.id}`)}
                    className="flex items-start justify-between gap-2"
                  >
                    <div className="min-w-0 flex-1">
                      <h3
                        className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate"
                        translate="no"
                      >
                        {item.nome}
                      </h3>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1">
                        <Users className="w-3 h-3" /> {item.devolvidos} de {item.total_alunos}{' '}
                        devolveram
                      </p>
                      <div className="mt-2 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${pendentes === 0 && item.total_alunos > 0 ? 'bg-green-500' : 'bg-blue-500'}`}
                          style={{
                            width:
                              item.total_alunos > 0
                                ? `${Math.round((item.devolvidos / item.total_alunos) * 100)}%`
                                : '0%',
                          }}
                        />
                      </div>
                      {pendentes > 0 && (
                        <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                          {pendentes} falta{pendentes === 1 ? '' : 'm'} devolver
                        </p>
                      )}
                    </div>
                    {podeExcluir && (
                      <button
                        onClick={(e) => apagar(e, item)}
                        title="Apagar item"
                        className="w-7 h-7 flex-shrink-0 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <Modal
        isOpen={modalAberto}
        onClose={() => setModalAberto(false)}
        title="Novo Item"
        maxWidth="sm"
      >
        <form onSubmit={criar} className="space-y-4">
          <Input
            label="Nome do item"
            value={nomeForm}
            onChange={(e) => setNomeForm(e.target.value)}
            placeholder="Ex.: Camisa do uniforme"
            required
            autoFocus
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalAberto(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={salvando}>
              {salvando ? 'Criando...' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Devolucoes;
